import { backstageClient } from "@nexxt/api-client/backstage";
import { atMostEvery, createQueryClient, liveCapture } from "@nexxt/app-kit";
import type { QueryClient } from "@tanstack/react-query";
import { loginHref } from "./features/auth/sign-in";

let signingIn = false;

/** No session, or it expired or was revoked: the gateway's Logto login (ADR-037), then back to the same page. */
function signIn(returnTo = `${location.pathname}${location.search}`): void {
  if (signingIn) return;
  signingIn = true;
  location.assign(loginHref(returnTo));
}

export const { api, auth } = backstageClient({ ...liveCapture, onUnauthenticated: () => signIn() });

/** The prefix of the counts of the home queues (ADR-136): one query for all, under no domain's key. */
export const WORK_QUEUES_KEY = ["work-queues"] as const;

/**
 * The counts are one call to every domain, and every live write signals their room too (ADR-285): read again no
 * sooner than this after the last time, however the app learnt of the change.
 */
const COUNTS_EVERY_MS = 15_000;

const [readQueues] = atMostEvery(
  COUNTS_EVERY_MS,
  () => void queryClient.invalidateQueries({ queryKey: WORK_QUEUES_KEY }),
);

let queuesStale = false;

/**
 * A change of any domain can move an item in or out of a queue, and the counts are under no domain's key: they are
 * read again whenever the app learns that a domain's data changed (once per tick), or their room says so.
 */
export function refreshQueues(): void {
  if (queuesStale) return;
  queuesStale = true;
  queueMicrotask(() => {
    queuesStale = false;
    readQueues(false);
  });
}

export const queryClient: QueryClient = createQueryClient();

// what tells the app a domain changed: a key invalidated after a mutation or an outcome a screen saw (a sync, an
// import, an allocation run), or a cache patched by hand with a mutation's answer
queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== "updated" || event.query.queryKey[0] === WORK_QUEUES_KEY[0]) return;
  const { action } = event;
  if (action.type === "invalidate" || (action.type === "success" && action.manual)) refreshQueues();
});
